import React from 'react';
import { Banknote, Wallet, ScanLine, ShoppingBag, Receipt } from 'lucide-react';
import abMark from '../../assets/images/ab-logo.png';
import useReveal, { reveal } from '../../hooks/useReveal';

const FLOWS = [
  {
    icon: Banknote,
    title: 'School fees, paid online',
    body: 'Parents settle bills from the app. Balances update the moment the money lands.',
  },
  {
    icon: Wallet,
    title: 'Student wallets',
    body: 'Parents top up a wallet for their child instead of sending cash to school.',
  },
  {
    icon: ScanLine,
    title: 'Canteen on a student ID',
    body: 'Students buy lunch with their card. No cash changes hands on the compound.',
  },
  {
    icon: ShoppingBag,
    title: 'Bookstore orders',
    body: 'Uniforms, books and supplies ordered and paid for through AtadaBoard.',
  },
  {
    icon: Receipt,
    title: 'Receipts and reconciliation',
    body: 'Every payment writes its own record, so the accounts add up at the end of term.',
  },
];

const Cashless = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section id="payments" className="ab-section ab-section--tint ab-cash" ref={ref}>
      <div className="ab-shell ab-cash__grid">
        <div className={`ab-cash__copy ${reveal(isVisible)}`}>
          <p className="ab-eyebrow">Online and cashless</p>
          <h2 className="ab-h2">Take your school cashless</h2>
          <p className="ab-lead">
            Fees, canteen and the bookstore all run through the same account, so money stops moving
            around the school in envelopes.
          </p>

          <ul className="ab-cash__flows">
            {FLOWS.map((flow, index) => {
              const Icon = flow.icon;
              return (
                <li
                  key={flow.title}
                  className={reveal(isVisible)}
                  style={{ '--ab-reveal-delay': `${120 + index * 70}ms` }}
                >
                  <span className="ab-cash__icon">
                    <Icon size={18} strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="ab-cash__flowTitle">{flow.title}</h3>
                    <p className="ab-body">{flow.body}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={`ab-cash__art ${reveal(isVisible)}`} style={{ '--ab-reveal-delay': '100ms' }}>
          {/*
            A sample student ID card, drawn in CSS. It stands in for a physical
            object rather than for a screen, and the name and number match the
            sample student in the product screenshots elsewhere on this page.
          */}
          <div className="ab-cash__card" aria-hidden="true">
            <div className="ab-cash__cardTop">
              <img src={abMark} alt="" width="400" height="304" />
              <span>Student ID</span>
            </div>
            <div className="ab-cash__cardName">Andra Lartey</div>
            <div className="ab-cash__cardMeta">
              <span className="ab-num">ID 134567</span>
              <ScanLine size={26} strokeWidth={1.5} />
            </div>
          </div>
          <p className="ab-cash__caption">
            One card for the canteen and the bookstore, funded from the parent’s app.
          </p>
        </div>
      </div>

      <style jsx>{`
        .ab-cash__grid {
          display: grid;
          grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
          gap: clamp(2.5rem, 5vw, 4.5rem);
          align-items: start;
        }

        .ab-cash__copy .ab-lead { margin-top: 0.875rem; }

        .ab-cash__flows {
          list-style: none;
          margin: 2.25rem 0 0;
          padding: 0;
          display: grid;
          gap: 1.375rem;
        }

        .ab-cash__flows li {
          display: flex;
          gap: 0.875rem;
        }

        .ab-cash__icon {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: var(--ab-r-inner);
          background: var(--ab-paper);
          border: 1px solid var(--ab-blue-100);
          color: var(--ab-blue);
        }

        .ab-cash__flowTitle {
          font-family: var(--ab-font-display);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: -0.015em;
          margin-bottom: 0.25rem;
        }

        .ab-cash__flows .ab-body { font-size: 0.9375rem; }

        .ab-cash__art {
          position: sticky;
          top: 110px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
        }

        .ab-cash__card {
          width: 100%;
          max-width: 340px;
          aspect-ratio: 1.586;
          padding: 1.375rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          border-radius: var(--ab-r-card);
          color: #fff;
          background: linear-gradient(145deg, var(--ab-blue) 0%, var(--ab-navy) 100%);
          box-shadow: var(--ab-shadow-lg);
          transform: rotate(-3deg);
        }

        .ab-cash__cardTop {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .ab-cash__cardTop img {
          height: 26px;
          width: auto;
          /* The mark is solid brand blue; invert it to read on the card. */
          filter: brightness(0) invert(1);
          opacity: 0.95;
        }

        .ab-cash__cardTop span {
          font-size: 0.6875rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255, 255, 255, 0.72);
        }

        .ab-cash__cardName {
          font-family: var(--ab-font-display);
          font-size: 1.375rem;
          font-weight: 600;
          letter-spacing: -0.02em;
        }

        .ab-cash__cardMeta {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          font-size: 0.9375rem;
          color: rgba(255, 255, 255, 0.82);
        }

        .ab-cash__caption {
          font-size: 0.9375rem;
          color: var(--ab-ink-3);
          text-align: center;
          max-width: 30ch;
          text-wrap: pretty;
        }

        @media (max-width: 1024px) {
          .ab-cash__grid { grid-template-columns: minmax(0, 1fr); }
          .ab-cash__art { position: static; }
        }

        @media (max-width: 620px) {
          .ab-cash__card { transform: none; }
        }
      `}</style>
    </section>
  );
};

export default Cashless;
